import {
    afterRender,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    effect,
    inject,
    input,
    ViewChild,
} from "@angular/core";
import { DecodeHTMLStringPipe } from "../../../pipes/decode-htmlstring.pipe";
import { MatIcon } from "@angular/material/icon";
import { MatIconButton } from "@angular/material/button";
import {
    MatNestedTreeNode,
    MatTree,
    MatTreeNodeDef,
    MatTreeNodeOutlet,
    MatTreeNodeToggle,
} from "@angular/material/tree";
import { PublicationModel } from "../../../interfaces/publication-model";
import {
    SelectedMap,
    TreeNodeModel,
} from "../../../interfaces/tree-node-model";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { PublicationFacade } from "../../../store/facade/publication.facade";

enum LevelTypePath {
    SECTION = "/s:",
    SECTION_RESOURCES = "/r:",
}

@Component({
    standalone: true,
    selector: "gwo-copy-tree",
    imports: [
        DecodeHTMLStringPipe,
        MatIcon,
        MatIconButton,
        MatNestedTreeNode,
        MatTree,
        MatTreeNodeDef,
        MatTreeNodeOutlet,
        MatTreeNodeToggle,
        MatMenu,
        MatMenuTrigger,
        MatMenuItem,
    ],
    templateUrl: "./copy-tree.component.html",
    styleUrl: "./copy-tree.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CopyTreeComponent {
    readonly publication = input<PublicationModel | null>(null);

    readonly title = input<string>("");

    readonly side = input<"left" | "right">("left");

    @ViewChild(MatMenuTrigger) contextMenuTrigger!: MatMenuTrigger;

    @ViewChild("tree") tree!: MatTree<TreeNodeModel>;

    private readonly publicationFacade = inject(PublicationFacade);

    readonly clickedElements = new SelectedMap();

    private nodePathMap = new WeakMap<TreeNodeModel, string>();

    private readonly cdr = inject(ChangeDetectorRef);

    private expandedKeys = new Set<string>();

    private pendingExpandedKeys: Set<string> | null = null;

    contextMenuPosition = { x: "0px", y: "0px" };

    private contextMenuNode: TreeNodeModel | null = null;

    constructor() {
        effect(() => {
            const pub = this.publication();

            if (pub?.sections) {
                this.nodePathMap = this.buildPathMap(pub.sections, "");
            } else {
                this.nodePathMap = new WeakMap<TreeNodeModel, string>();
            }

            if (this.pendingExpandedKeys) {
                this.pendingExpandedKeys.forEach((k) =>
                    this.expandedKeys.add(k),
                );
                this.pendingExpandedKeys = null;
            }
        });

        afterRender(() => {
            const pub = this.publication();
            if (!pub?.sections || !this.expandedKeys.size) return;

            this.restoreExpandedRecursive(
                pub.sections as TreeNodeModel[],
                this.expandedKeys,
            );
        });
    }

    public addExpandedKeys(keys: Set<string>): void {
        this.pendingExpandedKeys = keys;
    }

    public getExpandedKeys(): Set<string> {
        const expanded = new Set<string>();
        const pub = this.publication();
        if (!pub?.sections) return expanded;
        this.collectExpandedKeys(pub.sections as TreeNodeModel[], expanded);
        this.expandedKeys = new Set(expanded);
        return expanded;
    }

    private collectExpandedKeys(
        nodes: TreeNodeModel[],
        expanded: Set<string>,
    ): void {
        nodes.forEach((node) => {
            if (this.tree.isExpanded(node)) {
                expanded.add(this.getNodeKey(node));
            }
            if (node.sections?.length) {
                this.collectExpandedKeys(
                    node.sections as TreeNodeModel[],
                    expanded,
                );
            }
        });
    }

    private restoreExpandedRecursive(
        nodes: TreeNodeModel[],
        keys: Set<string>,
        parentPath = "",
    ): void {
        nodes.forEach((node, index) => {
            const key = `${parentPath}${LevelTypePath.SECTION}${index}`;
            if (keys.has(key)) {
                this.tree.expand(node);
            }
            if (node.sections?.length) {
                this.restoreExpandedRecursive(
                    node.sections as TreeNodeModel[],
                    keys,
                    key,
                );
            }
        });
    }

    childrenAccessor = (node: TreeNodeModel): TreeNodeModel[] =>
        node.sections ? node.sections : node.sectionResources || [];

    hasChild = (_: number, node: TreeNodeModel): boolean =>
        (!!node.sections && node.sections.length > 0) ||
        (!!node.sectionResources && node.sectionResources.length > 0);

    private buildPathMap(
        nodes: TreeNodeModel[],
        parentPath: string,
    ): WeakMap<TreeNodeModel, string> {
        const map = new WeakMap<TreeNodeModel, string>();
        this.buildPathMapRecursive(nodes, parentPath, map);
        return map;
    }

    private buildPathMapRecursive(
        nodes: TreeNodeModel[],
        parentPath: string,
        map: WeakMap<TreeNodeModel, string>,
    ): void {
        nodes.forEach((n, index) => {
            const key = `${parentPath}${LevelTypePath.SECTION}${index}`;
            map.set(n, key);

            if (n.sections && n.sections.length) {
                this.buildPathMapRecursive(n.sections, key, map);
            }

            if (n.sectionResources && n.sectionResources.length) {
                n.sectionResources.forEach((res, rIndex) => {
                    const rKey = `${key}${LevelTypePath.SECTION_RESOURCES}${rIndex}`;
                    map.set(res, rKey);
                });
            }
        });
    }

    getNodeKey(node: TreeNodeModel): string {
        return this.nodePathMap.get(node) ?? node.id.toString();
    }

    onNodeClick($event: MouseEvent, node: TreeNodeModel): void {
        $event.stopPropagation();
        const key = this.getNodeKey(node);
        const curr = this.clickedElements.get(key);
        this.clickedElements.set(key, !curr);
    }

    onNodeDBLClick($event: MouseEvent, node: TreeNodeModel): void {
        $event.stopPropagation();
        const nodeKey = this.getNodeKey(node);
        if (!this.clickedElements.has(nodeKey)) return;

        const curr = this.clickedElements.get(nodeKey);

        const updateChildren = (sections: TreeNodeModel[]) => {
            sections.forEach((sec) => {
                const secKey = this.getNodeKey(sec);
                this.clickedElements.set(secKey, curr);
                if (sec.sectionResources) {
                    sec.sectionResources.forEach((res) => {
                        const resKey = this.getNodeKey(res);
                        this.clickedElements.set(resKey, curr);
                    });
                }
            });
        };

        if (node.sections) {
            updateChildren(node.sections);
        } else if (node.sectionResources) {
            updateChildren(node.sectionResources);
        }
    }

    public clearSelection(): void {
        this.clickedElements.clear();
        this.cdr.markForCheck();
    }

    onNodeRightClick($event: MouseEvent, node: TreeNodeModel): void {
        $event.preventDefault();
        $event.stopPropagation();
        this.contextMenuPosition.x = `${$event.clientX}px`;
        this.contextMenuPosition.y = `${$event.clientY}px`;
        this.contextMenuNode = node;
        this.contextMenuTrigger.openMenu();
    }

    onCopySection(): void {
        if (!this.contextMenuNode) return;

        this.clickedElements.clear();

        const nodeKey = this.getNodeKey(this.contextMenuNode);
        this.clickedElements.set(nodeKey, true);

        if (this.contextMenuNode.sections) {
            this.selectChildren(
                this.contextMenuNode.sections as TreeNodeModel[],
            );
        } else if (this.contextMenuNode.sectionResources) {
            this.selectChildren(
                this.contextMenuNode.sectionResources as TreeNodeModel[],
            );
        }

        this.contextMenuNode = null;
        this.cdr.markForCheck();
    }

    private selectChildren(nodes: TreeNodeModel[]): void {
        nodes.forEach((node) => {
            const key = this.getNodeKey(node);
            this.clickedElements.set(key, true);

            if (node.sections?.length) {
                this.selectChildren(node.sections as TreeNodeModel[]);
            }
            if (node.sectionResources?.length) {
                this.selectChildren(node.sectionResources as TreeNodeModel[]);
            }
        });
    }

    onDeselectSection(): void {
        if (!this.contextMenuNode) return;

        const nodeKey = this.getNodeKey(this.contextMenuNode);
        this.clickedElements.set(nodeKey, false);

        if (this.contextMenuNode.sections) {
            this.deselectChildren(
                this.contextMenuNode.sections as TreeNodeModel[],
            );
        } else if (this.contextMenuNode.sectionResources) {
            this.deselectChildren(
                this.contextMenuNode.sectionResources as TreeNodeModel[],
            );
        }

        this.contextMenuNode = null;
        this.cdr.markForCheck();
    }

    private deselectChildren(nodes: TreeNodeModel[]): void {
        nodes.forEach((node) => {
            const key = this.getNodeKey(node);
            this.clickedElements.set(key, false);

            if (node.sections?.length) {
                this.deselectChildren(node.sections as TreeNodeModel[]);
            }
            if (node.sectionResources?.length) {
                this.deselectChildren(node.sectionResources as TreeNodeModel[]);
            }
        });
    }

    onNodeKeydown($event: KeyboardEvent): void {
        $event.preventDefault();
    }
}
